import { useCallback } from 'react'
import { StepType, Subtask, useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import { AIStepType } from 'components/TasksV2/AITaskCreation/AIFormStepContext'
import { useAiSubtaskDraft } from 'components/TasksV2/AITaskCreation/hooks/useAiSubtaskDraft'
import type { IAIPromptStepData } from 'components/TasksV2/AITaskCreation/hooks/useAiPromptForm'
import type { IAISubtaskStepData } from 'components/TasksV2/AITaskCreation/hooks/useAiPreviewForm'
import { mergeAiSubtask } from './mergeAiSubtask'
import { isDefaultSubtaskTitle } from 'components/TasksV2/TaskCreation/utils'

/** `{ index }` of the subtask being enhanced. Its own slice: `AISubtaskStep` replaces its draft on unmount. */
export const VM_AI_TARGET_KEY = 'AI_ENHANCE_TARGET'

export const VM_AI_STEPS: string[] = [AIStepType.AI_PROMPT, AIStepType.AI_PROMPT_GENERATOR, AIStepType.AI_SUBTASK]

/**
 * Drives the AI sub-flow embedded into VM task creation (modal flow):
 * step 1 → AI_PROMPT → AI_SUBTASK → back to step 1 with the subtask filled in.
 *
 * The parts shared with the full-page flow live in `useAiSubtaskDraft`; this hook adds the step-stack
 * navigation and the merge back into `SUBTASK_TASK_CREATION.sub_tasks[index]`.
 */
export const useVmAiEnhance = () => {
  const { formData, getFormData, updateFormData, setStepStack, subtaskActions } = useFormStepContext()
  const { seed, readPrompt, savePrompt, startGeneration, writeDraftFromLLM } = useAiSubtaskDraft()

  const readFormData = useCallback(() => (getFormData?.() ?? formData) as any, [getFormData, formData])

  /** Opens the sub-flow for `index`. `values` is the live step-1 form snapshot. */
  const openForSubtask = useCallback(
    (index: number, values: { title?: string; sub_tasks?: any[] }) => {
      if (subtaskActions?.startAiSetup) {
        subtaskActions.startAiSetup(index, values)
        return
      }
      // SubtaskCreationStep unmounts while the AI steps are open and has no unmount-save of its own,
      // so its current state has to be persisted here or everything typed on step 1 is lost.
      updateFormData(StepType.SUBTASK_TASK_CREATION, {
        title: values.title ?? '',
        sub_tasks: (values.sub_tasks ?? []) as Subtask[],
      })

      const subtask = (values.sub_tasks ?? [])[index] ?? {}

      seed({
        prompt: subtask.ai_prompt,
        referenceImages: subtask.reference_images,
        title: isDefaultSubtaskTitle(subtask.title) ? undefined : subtask.title,
        richText: subtask.content?.rich_text,
        images: subtask.content?.images,
      })
      updateFormData(VM_AI_TARGET_KEY as any, { index } as any)
      setStepStack((prev) => [...prev, AIStepType.AI_PROMPT])
    },
    [updateFormData, setStepStack, seed, subtaskActions]
  )

  /** "Next" on the prompt step: kick off generation and move on — the subtask step shows the loader. */
  const handlePromptNext = useCallback(
    (data: IAIPromptStepData) => {
      const index = readFormData()[VM_AI_TARGET_KEY]?.index

      savePrompt(data)
      startGeneration(data, `vm-subtask-${index}`)
      setStepStack((prev) => [...prev, AIStepType.AI_SUBTASK])
    },
    [readFormData, savePrompt, startGeneration, setStepStack]
  )

  /** "Done": merge the generated content into the target subtask and return to step 1. */
  const handleDone = useCallback(
    (data: IAISubtaskStepData) => {
      const all = readFormData()
      const index: number = all[VM_AI_TARGET_KEY]?.index ?? -1
      const step1 = (all[StepType.SUBTASK_TASK_CREATION] ?? { title: '', sub_tasks: [] }) as {
        title?: string
        sub_tasks: any[]
      }
      const promptDraft = readPrompt()
      const generated = data.sub_tasks?.[0] ?? {}

      const sub_tasks = (step1.sub_tasks ?? []).map((subtask: any, i: number) => {
        if (i !== index) return subtask
        return mergeAiSubtask(subtask, generated, promptDraft)
      })

      updateFormData(StepType.SUBTASK_TASK_CREATION, { ...step1, sub_tasks } as any)
      setStepStack((prev) => prev.filter((step) => !VM_AI_STEPS.includes(step)))
    },
    [readFormData, readPrompt, updateFormData, setStepStack]
  )

  return { openForSubtask, writeDraftFromLLM, handlePromptNext, handleDone }
}
